import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row starter-main mt-5 pt-5" }
const _hoisted_3 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4" }
const _hoisted_4 = { class: "d-flex align-items-center gap-2" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "pagination-mini" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled", "onClick"]
const _hoisted_9 = ["disabled"]

import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useCrud } from "@/composables/commen/useCrud";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import Swal from 'sweetalert2';
import apiService from "@/services/apiService";
import { toast } from "vue3-toastify";

// Manual debounce implementation
const API_RESOURCE = "/common/admin/support/tickets";

export default /*@__PURE__*/_defineComponent({
  __name: 'SupportTickets',
  props: {
  fixedStatus: {
    type: String,
    default: ""
  }
},
  setup(__props) {

const debounce = (fn: Function, delay: number) => {
  let timeout: any;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

const props = __props;

const router = useRouter();

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// Filters State
const searchQuery = ref("");
const filterStatus = ref(props.fixedStatus || "");
const filterPriority = ref("");

watch(() => props.fixedStatus, (newVal) => {
  filterStatus.value = newVal || "";
  tableConfig.value.showStatusFilter = !newVal;
  currentPage.value = 1;
  fetchTickets();
});

// pagination local UI state
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

// visiblePages computed
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Table configuration
const rows = ref<any[]>([]);
const tableConfig = ref({
  columns: [
    { name: "Name", field: "name", sortable: true }, 
    { name: "Subject", field: "subject", sortable: true },
    { name: "Category", field: "category", sortable: true },
    { name: "Priority", field: "priority", type: 'badge', sortable: true },
    { name: "Status", field: "status", type: 'badge', sortable: true },
    { name: "Created Date", field: "created_at", type: 'date', sortable: true },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  showStatusFilter: false,
  showPriorityFilter: true,
  showResetFilter: true,
  allStatusLables: ["open", "closed"],
  allPriorityLables: ["low", "medium", "high"],
  table_width: "100%",
});

watch(crud.data, (newVal) => {
  rows.value = newVal || [];
});

watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// Sync backend pagination into tablePagination
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
    tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const fetchTickets = async () => {
  const params: any = {
    page: currentPage.value,
    limit: tablePagination.value.per_page,
    status: props.fixedStatus || filterStatus.value || (props.fixedStatus ? undefined : "open"),
  };

  if (searchQuery.value) params.search = searchQuery.value;
  if (filterPriority.value) params.priority = filterPriority.value;

  await crud.fetchData(params);
};

const handleSearch = debounce(() => {
  currentPage.value = 1;
  fetchTickets();
}, 500);

const handleStatusFilter = (val: string) => {
  filterStatus.value = val.toLowerCase();
  currentPage.value = 1;
  fetchTickets();
};

const handlePriorityFilter = (val: string) => {
  filterPriority.value = val.toLowerCase();
  currentPage.value = 1;
  fetchTickets();
};

const resetFilters = () => {
  searchQuery.value = "";
  filterStatus.value = "";
  filterPriority.value = "";
  currentPage.value = 1;
  fetchTickets();
};

const changeRows = async () => {
  currentPage.value = 1;
  await fetchTickets();
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  currentPage.value = page;
  await fetchTickets();
};

const handleView = (row: any) => {
  const basePath = props.fixedStatus === 'closed' ? '/closed-tickets' : '/support-tickets';
  router.push(`${basePath}/${row._id}?mode=view`);
};

const handleEdit = (row: any) => {
  const basePath = props.fixedStatus === 'closed' ? '/closed-tickets' : '/support-tickets';
  router.push(`${basePath}/${row._id}`);
};

const handleDelete = async (data: any) => {
  const id = typeof data === 'string' ? data : data._id;
  try {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#21c1d6',
      cancelButtonColor: '#f8d7da',
      confirmButtonText: 'Yes, delete it!',
      customClass: {
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-outline-danger ms-2'
      },
      buttonsStyling: false
    });

    if (result.isConfirmed) {
      await apiService.delete(API_RESOURCE, id);
      toast.success("Ticket deleted successfully", { theme: "colored" });
      await fetchTickets();
    }
  } catch (err: any) {
    console.error("Failed to delete ticket:", err);
    toast.error(err.response?.data?.message || "Failed to delete ticket", { theme: "colored" });
  }
};

onMounted(async () => {
  await fetchTickets();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TableTitle, {
          title: "Support Tickets List",
          pageTitle: "Support Ticket",
          icon: "fas fa-ticket-alt",
          showAction: false
        }),
        _createVNode(CommonTable2, {
          tableObj: tableConfig.value,
          onEdit: handleEdit,
          onView: handleView,
          onDelete: handleDelete,
          onStatusFilter: handleStatusFilter,
          onPriorityFilter: handlePriorityFilter,
          onResetFilters: resetFilters,
          onSearch: _cache[0] || (_cache[0] = (val) => { searchQuery.value = val; _unref(handleSearch)(); }),
          permissionKey: "support"
        }, null, 8, ["tableObj"])
      ])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
        _withDirectives(_createElementVNode("select", {
          class: "form-select form-select-sm rows-select",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((tablePagination.value.per_page) = $event)),
          onChange: changeRows
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
            return _createElementVNode("option", {
              key: n,
              value: n
            }, _toDisplayString(n), 9, _hoisted_5)
          }), 64))
        ], 544), [
          [_vModelSelect, tablePagination.value.per_page]
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "page-btn prev_btn",
          disabled: !tablePagination.value.has_prev,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
        }, " « ", 8, _hoisted_7),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
          return (_openBlock(), _createElementBlock("button", {
            key: index,
            class: _normalizeClass(["page-btn", {
          'page-active': p === tablePagination.value.current_page,
          'disabled-dots': p === '...',
        }]),
            disabled: p === '...',
            onClick: ($event: any) => (p !== '...' && typeof p === 'number' && handlePageChange(p) )
          }, _toDisplayString(p), 11, _hoisted_8))
        }), 128)),
        _createElementVNode("button", {
          class: "page-btn next_btn",
          disabled: !tablePagination.value.has_next,
          onClick: _cache[3] || (_cache[3] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
        }, " » ", 8, _hoisted_9)
      ])
    ])
  ], 64))
}
}

})